/**
 * Hooks de gestão dos contatos autorizados de um Bond (LGPD ADR-129).
 *
 * Complementa o `useBondAuthorizedContacts` read-only de `use-bond.ts` (usado
 * pela aba Cliente para a contagem): aqui a listagem aceita `includeRevoked` e
 * há os 3 mutadores (add / update / revoke). Mutações usam optimistic-lock por
 * `version` (If-Match, ADR-071) e invalidam a chave de lista — o prefixo
 * `['bond-authorized-contacts', bondId]` casa tanto a contagem da aba quanto a
 * lista do drawer (incl. variante com includeRevoked).
 *
 * Gate `bond-basic` (server-side em cada endpoint). 403 → toast via api-client.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  type AddAuthorizedContactRequest,
  type BondAuthorizedContactWireItem,
  type RevokeAuthorizedContactRequest,
  type UpdateAuthorizedContactRequest,
} from '../lib/api-client';

const KEY = 'bond-authorized-contacts';

export function useManagedAuthorizedContacts(
  bondId: string | null,
  includeRevoked: boolean,
): {
  data: BondAuthorizedContactWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [KEY, bondId, { includeRevoked }],
    queryFn: async () => {
      if (bondId === null) throw new Error('bondId required');
      const res = await api.listBondAuthorizedContacts(bondId, { includeRevoked });
      return res.data;
    },
    enabled: bondId !== null,
    staleTime: 30_000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

function useInvalidateContacts(bondId: string | null): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [KEY, bondId] });
}

export function useAddAuthorizedContact(
  bondId: string | null,
): ReturnType<
  typeof useMutation<BondAuthorizedContactWireItem, Error, AddAuthorizedContactRequest>
> {
  const invalidate = useInvalidateContacts(bondId);
  return useMutation<BondAuthorizedContactWireItem, Error, AddAuthorizedContactRequest>({
    mutationFn: (body) => {
      if (bondId === null) throw new Error('bondId required');
      return api.addBondAuthorizedContact(bondId, body);
    },
    onSuccess: invalidate,
  });
}

interface UpdateVars {
  contactId: string;
  version: number;
  body: UpdateAuthorizedContactRequest;
}

export function useUpdateAuthorizedContact(
  bondId: string | null,
): ReturnType<typeof useMutation<BondAuthorizedContactWireItem, Error, UpdateVars>> {
  const invalidate = useInvalidateContacts(bondId);
  return useMutation<BondAuthorizedContactWireItem, Error, UpdateVars>({
    mutationFn: ({ contactId, version, body }) => {
      if (bondId === null) throw new Error('bondId required');
      return api.updateBondAuthorizedContact(bondId, contactId, version, body);
    },
    onSuccess: invalidate,
  });
}

interface RevokeVars {
  contactId: string;
  version: number;
  body: RevokeAuthorizedContactRequest;
}

export function useRevokeAuthorizedContact(
  bondId: string | null,
): ReturnType<typeof useMutation<BondAuthorizedContactWireItem, Error, RevokeVars>> {
  const invalidate = useInvalidateContacts(bondId);
  return useMutation<BondAuthorizedContactWireItem, Error, RevokeVars>({
    mutationFn: ({ contactId, version, body }) => {
      if (bondId === null) throw new Error('bondId required');
      return api.revokeBondAuthorizedContact(bondId, contactId, version, body);
    },
    onSuccess: invalidate,
  });
}
